
import { FileText, MonitorSmartphone, WrenchIcon, Settings, Layers, BarChart, UsersIcon, AppWindow, Tags, Users, FileSearch, ShieldCheck, ShieldAlert, ClipboardList } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const mainItems = [{
  title: "Getting Started",
  url: "/getting-started",
  icon: FileText
}, {
  title: "Devices",
  url: "/devices",
  icon: MonitorSmartphone
}, {
  title: "Dashboard",
  url: "/analytics",
  icon: BarChart
}, {
  title: "Apps",
  url: "/applications",
  icon: AppWindow
}, {
  title: "Patching",
  url: "/patching",
  icon: WrenchIcon
}, {
  title: "Vulnerabilities",
  url: "/vulnerabilities",
  icon: ShieldAlert
}, {
  title: "Configurations",
  url: "/configurations",
  icon: Layers
}, {
  title: "Tags",
  url: "/tags",
  icon: Tags
}, {
  title: "Logs",
  url: "/logs",
  icon: FileSearch
}];

const adminItems = [{
  title: "Users & Roles",
  url: "/admin/users-and-roles",
  icon: Users
}, {
  title: "Enrollment Profiles",
  url: "/admin/enrollment-profiles",
  icon: ClipboardList
}, {
  title: "Settings",
  url: "/settings",
  icon: Settings
}];

export function AppSidebar() {
  const { displayMode, open, isMobile } = useSidebar();
  const location = useLocation();
  
  const isActive = (url: string) => location.pathname === url || location.pathname.startsWith(url + '/');

  // Don't render content when sidebar is collapsed on DESKTOP only
  if (!open && !isMobile) {
    return (
      <Sidebar className="border-r border-brand-auxiliary1/10 md:mt-16 overflow-hidden transition-all duration-300 ease-out">
        <SidebarContent className="pt-4 md:pt-8" />
      </Sidebar>
    );
  }

  const renderMenuItem = (item: typeof mainItems[0]) => {
    const isInMiniMode = open && displayMode === "mini";
    const active = isActive(item.url);
    
    if (isInMiniMode) {
      return (
        <TooltipProvider key={item.title}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarMenuButton asChild isActive={active}>
                <Link to={item.url} className={cn(
                  "w-full justify-center px-2 transition-all duration-150",
                  active && "animate-fade-in border-l-3 border-l-primary bg-primary/10 rounded-md"
                )}>
                  <item.icon 
                    className={cn("h-[18px] w-[18px]", active ? "text-primary" : "text-foreground")} 
                    strokeWidth={active ? 2.5 : 2}
                  />
                 </Link>
               </SidebarMenuButton>
             </TooltipTrigger>
             <TooltipContent side="right">
               <p>{item.title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <SidebarMenuButton asChild isActive={active}>
        <Link to={item.url} className={cn(
          "transition-all duration-150",
          active && "animate-fade-in border-l-3 border-l-primary"
        )}>
          <item.icon className={cn("h-4 w-4", active ? "text-primary" : "text-foreground")} />
          <span className={cn("text-foreground", active && "font-semibold text-primary")}>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    );
  };

  return (
    <Sidebar className="border-r border-brand-auxiliary1/10 md:mt-16 transition-all duration-200 ease-out">
      <SidebarContent className="pt-4 md:pt-8 px-2 transition-opacity duration-200 ease-out">
        <SidebarGroup>
          {((open && displayMode === "expanded") || isMobile) && (
            <div className="px-3 py-2 text-xs font-semibold text-foreground/70 uppercase tracking-wide backdrop-blur-sm bg-muted/50 rounded-lg border border-border mb-2">Platform</div>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item)}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        <SidebarGroup>
          {((open && displayMode === "expanded") || isMobile) && (
            <div className="px-3 py-2 text-xs font-semibold text-foreground/70 uppercase tracking-wide backdrop-blur-sm bg-muted/50 rounded-lg border border-border mb-2">Administration</div>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {adminItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item)}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
